import { useState, useEffect } from "react";

/**
 * Tailwind breakpoints matching the search results grid:
 * grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4
 */
const BREAKPOINTS = [
  { minWidth: 1280, cols: 4 }, // xl
  { minWidth: 1024, cols: 3 }, // lg
  { minWidth: 768, cols: 2 }, // md
  { minWidth: 0, cols: 1 }, // mobile
];

function getGridCols(): number {
  const width = window.innerWidth;
  for (const bp of BREAKPOINTS) {
    if (width >= bp.minWidth) return bp.cols;
  }
  return 1;
}

/**
 * Returns the 0-based card index after which the inline FYP should render,
 * computed from the 1-based row number and the current grid column count.
 *
 * Returns null during SSR or before the first client-side measurement.
 */
export default function useInlineRowPosition(afterRow: number): number | null {
  const [position, setPosition] = useState<number | null>(null);

  useEffect(() => {
    if (afterRow <= 0) {
      setPosition(null);
      return;
    }

    function updatePosition() {
      const cols = getGridCols();
      setPosition(afterRow * cols - 1);
    }

    updatePosition();

    let debounceTimer: ReturnType<typeof setTimeout>;
    function handleResize() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updatePosition, 150);
    }

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [afterRow]);

  return position;
}
